import compact from 'lodash/compact'
import { useGetPondCaretakers } from 'modules/member/api/queries'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { DISEASE_GROUP_ORDER, SUB_GROUPS } from '../components/DiseaseDashboard/constants'
import { isDiseaseGroup } from '../components/DiseaseDashboard/utils'
import { SpeciesType } from '../constants'
import {
  useGetPonds,
  useGetRevampPondDashboardPagination,
  useDiseaseDashboardLabTablePagination,
  useShrimpSizeTablePagination,
  useGetPondMedia,
} from './queries'
import { CycleCaretaker, DashboardTableRow, GroupedRow, PondMediaFilter } from './types'

export const usePondInfoById = (pondId: string) => {
  const { data } = useGetPonds()
  const pond = data?.find((item) => pondId === item.id)
  return pond
}

export const usePondCaretakers = () => {
  const { data, ...rest } = useGetPondCaretakers()

  return useMemo(
    () => ({
      ...rest,
      data: (data ?? []).map((caretaker) => ({
        id: caretaker.id,
        name: caretaker.name,
        photo: caretaker?.pictureURL,
      })) as CycleCaretaker[] | [],
    }),

    [data, rest.isFetching]
  )
}

export const useSpeciesName = () => {
  const { pondId } = useParams()
  const pond = usePondInfoById(pondId ?? '')

  return pond?.species?.name ?? ''
}

export const useSpeciesId = () => {
  const { pondId } = useParams()
  const pond = usePondInfoById(pondId ?? '')

  return pond?.species?.id ?? 0
}

export const useIsShrimpSpecie = () => {
  const { pondId } = useParams()
  const pond = usePondInfoById(pondId ?? '')

  return pond?.species?.id === 1
}

export const useIsFishSpecie = () => {
  const { pondId } = useParams()
  const pond = usePondInfoById(pondId ?? '')

  return pond?.species?.id === 2
}

export const useGetXTitleLineChart = () => {
  const { t } = useTranslation('pond')
  const speciesName = useSpeciesName()

  return `${t('age')}${speciesName} (${t('day')})`
}

export const useGetSpeciesSizeUnit = () => {
  const { pondId } = useParams()
  const pond = usePondInfoById(pondId ?? '')
  const { t } = useTranslation('pond')

  if (pond?.species?.id && pond.species.id === SpeciesType.Fish) {
    return t('gram.per.one')
  }

  return t('amount.per.kg.unit')
}

export const usePondDashboardTableSelector = () => {
  const { data, ...rest } = useGetRevampPondDashboardPagination()
  const columns = data?.pages.reduce(
    (acc: string[], page) => acc.concat(page.data.dashboard.columns),
    []
  )

  const mergedMap = new Map<string, DashboardTableRow>()
  const pages = data?.pages
  if (pages != null) {
    for (const page of pages) {
      for (const row of page.data.dashboard.rows) {
        const key = `${row.group}_${row.label}`
        if (!mergedMap.has(key)) {
          mergedMap.set(key, {
            ...row,
            id: [...row.id],
            values: [...row.values],
          })
        } else {
          mergedMap.get(key)!.id.push(...row.id)
          mergedMap.get(key)!.values.push(...row.values)
        }
      }
    }
  }

  return useMemo(
    () => ({
      ...rest,
      data: {
        columns: (columns ?? []).reverse(),
        rows: Array.from(mergedMap.entries()).map(([key, v]) => ({
          ...v,
          id: v.id.reverse(),
          values: v.values.reverse(),
        })),
      },
    }),
    [data]
  )
}

export const useDiseaseLabTableSelector = () => {
  const { data, ...rest } = useDiseaseDashboardLabTablePagination()

  return useMemo(() => {
    const pages = data?.pages

    if (!pages?.length) {
      return {
        ...rest,
        data: {
          columns: [],
          rows: [],
          title: '',
        },
      }
    }

    const columns: string[] = []
    const mergedMap = new Map<string, any>()

    // Single pass through all pages
    for (const page of pages) {
      // Some farms return `columns: null` / `rows: null` when there is no lab
      // data yet — spreading/iterating null crashes the page (white screen).
      const pageColumns = page.data.dashboard.columns ?? []
      const pageRows = page.data.dashboard.rows ?? []

      columns.push(...pageColumns)

      for (const row of pageRows) {
        // Hide liver/stomach culture rows for the lab-table ponds; only the
        // water row (plus the shrimp-age row, which has no subGroup) remains.
        if (
          isDiseaseGroup(row.group) &&
          (row.subGroup === SUB_GROUPS.LIVER || row.subGroup === SUB_GROUPS.STOMACH)
        ) {
          continue
        }

        const key = row.subGroup
          ? `${row.group}_${row.subGroup}_${row.label}`
          : `${row.group}_${row.label}`

        if (!mergedMap.has(key)) {
          mergedMap.set(key, {
            ...row,
            id: [...row.id],
            values: [...row.values],
          })
        } else {
          const existing = mergedMap.get(key)!
          existing.id.push(...row.id)
          existing.values.push(...row.values)
        }
      }
    }

    // NK-198: order groups seawater → before-filter → holding pond. Array
    // sort is stable, so each group's rows (liver/stomach/water + age) and the
    // column order stay intact; only the group blocks are reordered.
    const rows = Array.from(mergedMap.values())
      .map((v) => ({
        ...v,
        id: v.id.reverse(),
        values: v.values.reverse(),
      }))
      .sort(
        (a, b) =>
          (DISEASE_GROUP_ORDER[a.group] ?? Number.MAX_SAFE_INTEGER) -
          (DISEASE_GROUP_ORDER[b.group] ?? Number.MAX_SAFE_INTEGER)
      )

    return {
      ...rest,
      data: {
        columns: columns.reverse(),
        rows,
        title: pages[0]?.data.dashboard.title ?? '',
      },
    }
  }, [data?.pages, rest.isLoading, rest.isFetching, rest.hasNextPage, rest.isFetchingNextPage])
}

// NK-276: merges the ไซส์กุ้ง pages into a single table. Simpler than the lab
// selector — there are no rows to hide and no group reordering, because the
// backend already emits exactly nine rows per pond in display order. Pages arrive
// newest-date-first, so both the columns and each row's values are reversed once
// at the end to render oldest → newest.
export const useShrimpSizeTableSelector = () => {
  const { data, ...rest } = useShrimpSizeTablePagination()

  return useMemo(() => {
    const pages = data?.pages

    if (!pages?.length) {
      return { ...rest, pageCount: 0, data: { columns: [], rows: [], title: '' } }
    }

    const columns: string[] = []
    const mergedMap = new Map<string, GroupedRow>()

    for (const page of pages) {
      // A farm with no shrimp-size records at all returns null rather than [];
      // iterating that white-screens the page (same trap as the lab table).
      const pageColumns = page.data.dashboard.columns ?? []
      const pageRows = page.data.dashboard.rows ?? []

      columns.push(...pageColumns)

      for (const row of pageRows) {
        const key = `${row.group}_${row.label}`
        const existing = mergedMap.get(key)
        if (!existing) {
          mergedMap.set(key, { ...row, id: [...row.id], values: [...row.values] })
        } else {
          existing.id.push(...row.id)
          existing.values.push(...row.values)
        }
      }
    }

    const rows = Array.from(mergedMap.values()).map((row) => ({
      ...row,
      id: [...row.id].reverse(),
      values: [...row.values].reverse(),
    }))

    return {
      ...rest,
      pageCount: pages.length,
      data: {
        columns: [...columns].reverse(),
        rows,
        title: pages[0]?.data.dashboard.title ?? '',
      },
    }
  }, [
    data?.pages,
    rest.isLoading,
    rest.isFetching,
    rest.hasNextPage,
    rest.isFetchingNextPage,
  ])
}

export const usePondAlbumMedia = () => {
  const { data, ...rest } = useGetPondMedia()

  return useMemo(
    () => ({
      ...rest,
      data: compact((data?.pages ?? []).map((page) => page.medias).flat()),
    }),
    [data]
  )
}
